/**
 * Seeded Monte-Carlo all-in equity for a concrete hand on any street.
 *
 * Pure and deterministic: given the same inputs (including derived seed) it returns
 * the same number every call, so repeated advisor evaluations of one spot are stable.
 * Uses the same `pokersolver` engine the app evaluates real showdowns with.
 *
 * This is intentionally postflop-oriented: preflop strength should use the precomputed
 * `getPreflopEquity` table (lower variance, zero cost). Monte-Carlo shines once a board
 * exists and exact opponent count + board texture matter.
 */

import { Hand, type CardString } from 'pokersolver'
import { Suits, Values } from './cardConstants'

const FULL_DECK: string[] = Values.flatMap((value) => Suits.map((suit) => `${value}${suit}`))
const BOARD_SIZE = 5

/**
 * Small, fast, deterministic PRNG (mulberry32) seeded by a 32-bit integer.
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0
  return function next(): number {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * djb2 string hash → 32-bit seed, so a spot's cards produce a stable seed.
 */
function hashSeed(input: string): number {
  let hash = 5381
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33) ^ input.charCodeAt(index)
  }
  return hash >>> 0
}

/**
 * Partial Fisher-Yates: draw `count` cards from `deck` using `rng` without mutating
 * the caller's array. Only the prefix we need is shuffled.
 */
function drawCards(deck: string[], count: number, rng: () => number): string[] {
  const pool = [...deck]
  const drawn: string[] = []
  for (let index = 0; index < count && pool.length > 0; index += 1) {
    const swap = index + Math.floor(rng() * (pool.length - index))
    const temp = pool[index]
    pool[index] = pool[swap]
    pool[swap] = temp
    drawn.push(pool[index])
  }
  return drawn
}

export type MonteCarloEquityArgs = {
  holeCards: string[]
  communityCards: string[]
  opponents: number
  /** Number of random runouts to simulate. Higher = lower variance, more CPU. */
  samples?: number
  /** Optional explicit seed; defaults to a hash of the known cards (deterministic). */
  seed?: number
}

/**
 * Estimate hero's all-in pot equity (win + tie share, 0–1) against `opponents` random
 * hands across `samples` simulated runouts.
 */
export function estimateEquityMonteCarlo({
  holeCards,
  communityCards,
  opponents,
  samples = 400,
  seed
}: MonteCarloEquityArgs): number {
  if (opponents <= 0) {
    return 1
  }

  const known = new Set([...holeCards, ...communityCards])
  const remaining = FULL_DECK.filter((card) => !known.has(card))
  const boardNeeded = BOARD_SIZE - communityCards.length
  const cardsPerSample = opponents * 2 + boardNeeded

  // Not enough cards to deal the requested table — cannot simulate meaningfully.
  if (boardNeeded < 0 || cardsPerSample > remaining.length) {
    return 0
  }

  const rng = mulberry32(seed ?? hashSeed([...holeCards, '|', ...communityCards, `|${opponents}`].join(',')))

  let score = 0
  for (let sample = 0; sample < samples; sample += 1) {
    const drawn = drawCards(remaining, cardsPerSample, rng)
    const board = [...communityCards, ...drawn.slice(0, boardNeeded)]

    const heroHand = Hand.solve([...holeCards, ...board] as CardString[])
    const hands = [heroHand]
    for (let opp = 0; opp < opponents; opp += 1) {
      const start = boardNeeded + opp * 2
      const oppHole = drawn.slice(start, start + 2)
      hands.push(Hand.solve([...oppHole, ...board] as CardString[]))
    }

    const winners = Hand.winners(hands)
    if (winners.includes(heroHand)) {
      score += 1 / winners.length
    }
  }

  return score / samples
}
